/**
 * Artifact Prompt Panel
 *
 * Chat interface for refining individual artifacts (skills, attributes, integrations)
 * Integrates with ChatInterface component to provide conversational artifact refinement
 */

import { useState, useEffect } from 'react';
import { useProjectStore } from '@/stores/projectStore';
import { Button } from '../ui/Button';
import { ChatInterface } from '../chat/ChatInterface';
import { MessageSquare, FileText } from 'lucide-react';

export const ArtifactPromptPanel: React.FC = () => {
  const activeProjectId = useProjectStore((state) => state.activeProjectId);
  const selectedArtifactId = useProjectStore((state) => state.ui.selectedArtifactId);
  const projects = useProjectStore((state) => state.projects);

  const project = activeProjectId ? projects[activeProjectId] : null;
  const artifact = project && selectedArtifactId ? project.artifacts[selectedArtifactId] : null;

  const [viewMode, setViewMode] = useState<'chat' | 'info'>('chat');

  if (!activeProjectId || !selectedArtifactId) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-background">
        <div className="text-center text-muted-foreground">
          <p className="text-sm">No artifact selected</p>
          <p className="text-xs mt-2">Select an artifact to refine it with AI</p>
        </div>
      </div>
    );
  }

  if (!artifact) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-background">
        <div className="text-center text-muted-foreground">
          <p className="text-sm">Artifact not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div>
          <h3 className="text-sm font-medium">
            {artifact.type === 'SKILL' ? 'Skill' :
             artifact.type === 'ATTRIBUTE' ? 'Attribute' :
             artifact.type === 'INTEGRATION' ? 'Integration' :
             artifact.type === 'SUBFLOW' ? 'Subflow' :
             'Artifact'} Prompt
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Chat with AI to refine this {artifact.type.toLowerCase()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex border border-border rounded-md">
            <button
              onClick={() => setViewMode('chat')}
              className={`px-3 py-1 text-xs flex items-center gap-1 rounded-l-md transition-colors ${
                viewMode === 'chat'
                  ? 'bg-orange-500 text-white'
                  : 'bg-background text-muted-foreground hover:text-foreground'
              }`}
            >
              <MessageSquare className="w-3 h-3" />
              Chat
            </button>
            <button
              onClick={() => setViewMode('info')}
              className={`px-3 py-1 text-xs flex items-center gap-1 rounded-r-md transition-colors ${
                viewMode === 'info'
                  ? 'bg-orange-500 text-white'
                  : 'bg-background text-muted-foreground hover:text-foreground'
              }`}
            >
              <FileText className="w-3 h-3" />
              Info
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {viewMode === 'chat' ? (
          <ChatInterface
            projectId={activeProjectId!}
            artifactId={selectedArtifactId!}
            showPlanShortcut
            onConversationComplete={(messages) => {
              console.log('[ArtifactPromptPanel] Conversation completed with', messages.length, 'messages');
              // Future: Trigger artifact plan generation here
            }}
          />
        ) : (
          <div className="h-full overflow-auto p-4">
            <div className="space-y-4">
              {/* Artifact Details */}
              <div>
                <h4 className="text-sm font-medium mb-2">Artifact Details</h4>
                <div className="bg-muted p-4 rounded-md border border-border space-y-2">
                  <div>
                    <span className="text-xs text-muted-foreground">Name:</span>
                    <p className="text-sm font-medium">{artifact.name}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">Type:</span>
                    <p className="text-sm">{artifact.type}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">Identifier:</span>
                    <p className="text-sm font-mono">{artifact.fullIdentifier}</p>
                  </div>
                  {artifact.description && (
                    <div>
                      <span className="text-xs text-muted-foreground">Description:</span>
                      <p className="text-sm">{artifact.description}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Seed Plan (if exists) */}
              {artifact.seedPlan && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Context from Master Plan</h4>
                  <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900/50 rounded-md p-4">
                    <p className="text-sm whitespace-pre-wrap">{artifact.seedPlan}</p>
                  </div>
                </div>
              )}

              {/* Instructions */}
              <div>
                <h4 className="text-sm font-medium mb-2">How to Use</h4>
                <div className="bg-muted p-4 rounded-md border border-border">
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                    <li>Switch to <strong>Chat</strong> mode to start refining this artifact</li>
                    <li>Discuss requirements, clarify details, and explore options</li>
                    <li>When ready, complete the conversation to generate an artifact plan</li>
                    <li>The plan will be used to generate LumaScript and final artifacts</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
